import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const ARTICLE = getArticleBySlug("/learn/social-media-marketing-small-business-colorado");
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/learn/social-media-marketing-small-business-colorado")({
  head: () => ({
    meta: [
      { title: "Social Media Marketing for Small Business in Colorado (2026)" },
      { name: "description", content: "Social media marketing for Colorado small businesses: which platforms matter, what to post, realistic results, and what management should cost." },
      { property: "og:title", content: "Social Media Marketing for Small Business in Colorado (2026)" },
      { property: "og:description", content: "Which platforms matter, what to post, realistic results, and what management should cost." },
      { property: "og:url", content: `${SITE_URL}/learn/social-media-marketing-small-business-colorado` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: ARTICLE?.title ?? "Social Media Marketing for Small Business",
        description: ARTICLE?.desc ?? "A practical social media guide for Colorado small businesses.",
      }),
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/learn/social-media-marketing-small-business-colorado" }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="Marketing Strategy"
      read="10 min read"
      title="Social media marketing for small business in Colorado"
      intro="You don't need to be on every platform, and you don't need to dance on camera. For a Colorado service business, social media has one job: keep you visible and credible so that when someone needs you — or asks an AI who to call — your name comes up. Here's how to do it without it eating your week."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "Social media for small business" },
      ]}
      path="/learn/social-media-marketing-small-business-colorado"
      faqs={[
        { q: "Which social platforms should a small business use?", a: "For most Colorado service businesses: Facebook and Instagram for local visibility, LinkedIn if you sell to other businesses, and your Google Business Profile (which is social media most owners forget). Two platforms done consistently beat five done sporadically." },
        { q: "How often should a small business post?", a: "Two to three times a week per platform is enough to stay visible. Consistency matters more than volume — a year of steady posting builds more trust than a burst followed by silence." },
        { q: "What does social media management cost?", a: "Real management — strategy, content creation, posting, and engagement — is included in our Growth plan at $1,650/mo alongside SEO and AI search optimization. Standalone 'posting services' under $300/mo are usually recycled generic content that does nothing for a local business." },
      ]}
    >
      <h2>Pick two platforms and ignore the rest</h2>
      <p>
        Platform choice follows your customer, not trends. Home services and local retail:
        Facebook and Instagram. Professional services and B2B: LinkedIn. Everyone: your Google
        Business Profile, which acts like a social channel — posts, photos, and updates there
        directly support your map rankings. A roofer in Colorado Springs gains nothing from
        TikTok dances but a lot from before-and-after photos on Facebook.
      </p>

      <h2>What to post (the simple version)</h2>
      <ul>
        <li><strong>Proof of work</strong> — finished jobs, before/afters, your crew on site. This is your best content and you already have it on your phone.</li>
        <li><strong>Answers</strong> — the questions customers ask you every week, answered in a sentence or two. These also feed AI search tools looking for clear answers.</li>
        <li><strong>Local proof</strong> — the neighborhoods you work in, local events, community involvement. Colorado customers hire local.</li>
        <li><strong>Reviews and thank-yous</strong> — share the kind words you earn.</li>
      </ul>

      <h2>Realistic expectations</h2>
      <p>
        Social media rarely rings the phone directly for a service business. What it does: builds
        the trust that turns a Google search into a call, keeps past customers remembering you,
        and creates the steady stream of real, local content that search engines and AI tools use
        to decide you&rsquo;re a legitimate, active business. We&rsquo;ve seen consistent posting
        take Colorado companies from a few hundred to 10,000–100,000+ views a month — but it
        compounds over months, not days.
      </p>

      <h2>Doing it yourself vs. hiring it out</h2>
      <p>
        DIY works if you&rsquo;ll actually do it every week — most owners start strong and fade
        by month two. Hiring it out makes sense when your hour is worth more than the management
        fee. Our <Link to="/pricing">Growth plan at $1,650/mo</Link> bundles social media with
        Local SEO, Google Maps, and AI search optimization, because these channels reinforce each
        other — the same content that fills your social feed strengthens your search presence.
      </p>

      <h2>Get help with this</h2>
      <p><Link to="/social-media-marketing-small-business-colorado">Small business social media management services</Link> explains our scope and the next step if you want this work done for your business.</p>

      <h2>What to do next</h2>
      <p>
        Read how <Link to="/learn/social-media-views-colorado-business">Colorado businesses grow
        social views</Link>, see what&rsquo;s included on our <Link to="/pricing">pricing
        page</Link>, or request a <Link to="/free-audit">free digital marketing audit</Link> —
        we&rsquo;ll look at your current social presence and tell you honestly whether it&rsquo;s
        worth your money yet.
      </p>
    </ArticleLayout>
  );
}
